import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cloud, Shield, Users, RefreshCw, Zap, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import HeroWithBanner from '../components/HeroWithBanner';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import ClientLogoSlider from '../components/ClientLogoSlider';
import AnimatedSection from '../components/AnimatedSection';
import { features, stats, deals } from '../mockData';
import { trackButtonClick, trackPageView } from '../utils/analytics';

const Home = ({ onOpenPopup }) => {
  useEffect(() => {
    trackPageView('/', 'Home');
  }, []);

  const handleCTAClick = (ctaName) => {
    trackButtonClick(ctaName, 'home_page');
    onOpenPopup();
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Banner */}
      <HeroWithBanner onOpenPopup={onOpenPopup} />

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <AnimatedSection key={index} animation="scaleIn" delay={index * 100}>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-[#0056D2] mb-2">{stat.value}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Features Highlights */}
      <section className="py-24 bg-gradient-to-b from-[#F5F7FA] to-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Why Choose TallyBharat?
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Powerful features designed to make your Tally experience seamless and secure
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const iconMap = { Cloud, Shield, Users, RefreshCw, Zap };
              const Icon = iconMap[feature.icon];
              return (
                <AnimatedSection key={index} animation="fadeInUp" delay={index * 100}>
                  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
                    <div className="bg-gradient-to-br from-[#0056D2] to-[#0A1F44] w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      {Icon && <Icon size={28} className="text-white" />}
                    </div>
                    <h3 className="text-xl font-semibold text-[#0A1F44] mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Us Section with Banner */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slideInLeft">
              <div className="space-y-6">
                <div className="inline-block bg-[#FF7A00]/10 text-[#FF7A00] px-4 py-2 rounded-full text-sm font-semibold">
                  Established 2004
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44]">
                  Tally Partner Since 2004
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  With over 21 years of experience, TallyBharat has been at the forefront of providing reliable, secure, and efficient Tally cloud hosting solutions to businesses across India.
                </p>
                <div className="flex items-center space-x-4 pt-4">
                  <CheckCircle2 className="text-[#0056D2] flex-shrink-0" size={24} />
                  <span className="text-gray-700 font-medium">Tally Partner</span>
                </div>
                <div className="flex items-center space-x-4">
                  <CheckCircle2 className="text-[#0056D2] flex-shrink-0" size={24} />
                  <span className="text-gray-700 font-medium">ISO Certified Infrastructure</span>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="slideInRight">
              <div className="relative">
                <img
                  src="https://tally-bharat.s3.ap-south-1.amazonaws.com/media/tb-media-a1.jpeg"
                  alt="Cloud Flexibility in 2025"
                  className="w-full h-auto rounded-3xl shadow-2xl"
                  style={{ maxWidth: '1200px', maxHeight: '1200px' }}
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#FF7A00] opacity-20 rounded-3xl -z-10 hidden lg:block" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Deals Section */}
      <section className="py-24 bg-gradient-to-b from-[#F5F7FA] to-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Our Popular Bundles
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Choose the perfect plan for your business needs
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {deals.map((deal, index) => (
              <AnimatedSection key={index} animation="scaleIn" delay={index * 150}>
                <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative overflow-hidden group">
                  {deal.badge && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-[#FF7A00] to-[#e66d00] text-white px-6 py-2 rounded-bl-3xl font-semibold text-sm">
                      {deal.badge}
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-[#0A1F44] mb-3 mt-4">{deal.title}</h3>
                  <p className="text-gray-600 mb-6 min-h-[48px]">{deal.description}</p>
                  <div className="text-4xl font-bold text-[#0056D2] mb-6">{deal.price}</div>
                  <ul className="space-y-3 mb-8">
                    {deal.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center space-x-3">
                        <CheckCircle2 size={20} className="text-[#0056D2] flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleCTAClick('know_more')}
                    className="w-full bg-[#0056D2] hover:bg-[#0A1F44] text-white font-semibold py-3 rounded-full transition-all duration-200 group-hover:scale-105"
                  >
                    Know More
                  </Button>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                What Our Clients Say
              </h2>
              <p className="text-xl text-gray-600">
                Trusted by thousands of businesses across India
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="scaleIn" delay={200}>
            <TestimonialsCarousel />
          </AnimatedSection>
        </div>
      </section>

      {/* Client Logos Section */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <AnimatedSection animation="fadeInUp">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A1F44] text-center mb-2">
              Trusted by Leading Organizations
            </h2>
            <p className="text-gray-600 text-center">Banks and financial institutions rely on our infrastructure</p>
          </AnimatedSection>
        </div>
        <AnimatedSection animation="fadeIn" delay={200}>
          <ClientLogoSlider />
        </AnimatedSection>
      </section>

      {/* Products & Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Our Products & Services
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Comprehensive cloud solutions tailored for your business
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Tally on Cloud',
              'Custom ERP on Cloud (Marg, Busy, Miracle, and more)',
              'Bare Metal Servers',
              'Dedicated Virtual Machines (Managed & Unmanaged)',
              'Backup Solutions (Acronis Partner)',
              'Custom Backup Solutions',
              'Tally Software Customization'
            ].map((service, index) => (
              <AnimatedSection key={index} animation="fadeInUp" delay={index * 50}>
                <div className="group bg-gradient-to-br from-white via-[#F5F7FA] to-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-[#0056D2] relative overflow-hidden">
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0056D2]/5 to-[#FF7A00]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
                  
                  <div className="relative flex items-start space-x-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0056D2] to-[#0A1F44] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-md">
                      <CheckCircle2 size={20} className="text-white" />
                    </div>
                    <div>
                      <span className="text-[#0A1F44] font-bold text-lg group-hover:text-[#0056D2] transition-colors duration-300 block mb-1">{service}</span>
                      <div className="h-1 w-0 bg-gradient-to-r from-[#0056D2] to-[#FF7A00] group-hover:w-full transition-all duration-500 rounded-full" />
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#0056D2] to-[#0A1F44] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FF7A00] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="fadeInUp">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Accounting Operations?
            </h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              Join 2000+ businesses already using TallyBharat. Get started in minutes!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a href="https://app.tallybharat.com/sign-up" target="_blank" rel="noopener noreferrer">
                <Button
                  onClick={() => trackButtonClick('start_free_trial', 'final_cta')}
                  className="bg-[#FF7A00] hover:bg-[#e66d00] text-white font-semibold px-10 py-6 text-lg rounded-full transition-all duration-200 hover:scale-105 hover:shadow-2xl"
                >
                  Start Free Trial
                </Button>
              </a>
              <Link to="/pricing">
                <Button
                  onClick={() => trackButtonClick('view_pricing', 'final_cta')}
                  variant="outline"
                  className="bg-white/10 hover:bg-white/20 text-white border-2 border-white font-semibold px-10 py-6 text-lg rounded-full transition-all duration-200 hover:scale-105 backdrop-blur-sm"
                >
                  View Pricing
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Home;